import {
  CSRF_HEADER,
  type ProjectDetail,
  type ProjectVisibility,
  type UpdateProjectRequest,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../../api.ts";
import { ok } from "../../api-errors.ts";
import { useSession } from "../../session.tsx";
import { useUnsavedWork } from "../../shell/session-ended.ts";
import { projectKeys } from "../queries.ts";
import { projectRefusal } from "./refusals.ts";

const RANK: Record<ProjectVisibility, number> = { private: 0, entity: 1, entity_tree: 2 };

/** What the section reads of a project (the backoffice passes its governed project). */
export type GeneralProject = Pick<
  ProjectDetail,
  "id" | "name" | "description" | "visibility" | "owner" | "ownerName"
>;

/**
 * Name, description and who can open the project. A personal project is always private;
 * widening the visibility first names the audience that gains viewer access. `save` replaces
 * the project route (the backoffice saves through governance).
 */
export function GeneralSection({
  project,
  editable,
  save: saveVia,
}: {
  project: GeneralProject;
  editable: boolean;
  save?: (json: UpdateProjectRequest) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [visibility, setVisibility] = useState<ProjectVisibility>(project.visibility);
  const [confirming, setConfirming] = useState(false);
  const personal = project.owner.kind === "user";
  const entity = project.ownerName ?? t("settings.general.ownerFallback");
  const options: ProjectVisibility[] = personal
    ? ["private"]
    : ["private", "entity", "entity_tree"];

  const body = (): UpdateProjectRequest => ({
    ...(name.trim() !== project.name ? { name: name.trim() } : {}),
    ...((description.trim() || null) !== project.description
      ? { description: description.trim() || null }
      : {}),
    ...(visibility !== project.visibility ? { visibility } : {}),
  });
  const save = useMutation({
    mutationFn: async (json: UpdateProjectRequest) =>
      saveVia
        ? saveVia(json)
        : ok(
            await api.api.projects[":projectId"].$patch(
              { param: { projectId: project.id }, json },
              { headers: { [CSRF_HEADER]: csrfToken } },
            ),
          ),
    onSuccess: () => setConfirming(false),
    // The header, sidebar, home composer and document title all read these.
    onSettled: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
  const changed = Object.keys(body()).length > 0;
  useUnsavedWork(editable && changed, `${name}\n\n${description}`);
  const submit = () => {
    if (!changed || save.isPending || !name.trim()) {
      return;
    }
    if (RANK[visibility] > RANK[project.visibility] && !confirming) {
      setConfirming(true);
      return;
    }
    save.mutate(body());
  };

  if (!editable) {
    return (
      <dl
        data-esi-content="projects"
        className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]"
      >
        <dt className="text-fg-muted">{t("settings.general.name")}</dt>
        <dd className="text-fg">{project.name}</dd>
        <dt className="text-fg-muted">{t("settings.general.description")}</dt>
        <dd className="whitespace-pre-wrap text-fg">
          {project.description || (
            <span className="text-fg-muted">{t("settings.general.noDescription")}</span>
          )}
        </dd>
        <dt className="text-fg-muted">{t("settings.general.visibility")}</dt>
        <dd className="flex flex-col gap-0.5">
          <span className="text-fg">{t(`project.visibility.${project.visibility}`)}</span>
          <span className="text-xs text-fg-muted">
            {personal
              ? t("settings.general.personalOnly")
              : t(`settings.general.visibilityHint.${project.visibility}`, { entity })}
          </span>
        </dd>
      </dl>
    );
  }

  return (
    <form
      data-esi-content="projects"
      className="flex max-w-xl flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("settings.general.name")}</Label>
        <Input
          id={`${id}-name`}
          required
          maxLength={200}
          value={name}
          onChange={(e) => {
            save.reset();
            setName(e.currentTarget.value);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-description`}>{t("settings.general.description")}</Label>
        <Textarea
          id={`${id}-description`}
          maxLength={2000}
          value={description}
          onChange={(e) => {
            save.reset();
            setDescription(e.currentTarget.value);
          }}
          className="min-h-20"
        />
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-medium text-fg">
          {t("settings.general.visibility")}
        </legend>
        <RadioGroup
          value={visibility}
          onValueChange={(v) => {
            save.reset();
            setVisibility(v as ProjectVisibility);
          }}
          className="gap-2.5"
        >
          {options.map((v) => (
            <div key={v} className="flex items-start gap-2.5">
              <RadioGroupItem
                id={`${id}-visibility-${v}`}
                value={v}
                aria-describedby={`${id}-visibility-${v}-hint`}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-0.5">
                <Label htmlFor={`${id}-visibility-${v}`}>{t(`project.visibility.${v}`)}</Label>
                <p id={`${id}-visibility-${v}-hint`} className="text-sm text-fg-secondary">
                  {t(`settings.general.visibilityHint.${v}`, { entity })}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
        {personal ? (
          <p className="text-sm text-fg-secondary">{t("settings.general.personalOnly")}</p>
        ) : null}
      </fieldset>
      {save.isError && !confirming ? (
        <Notice tone="danger">{projectRefusal(save.error)}</Notice>
      ) : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!changed || save.isPending || !name.trim()}>
          {save.isPending ? t("settings.saving") : t("settings.save")}
        </Button>
        {save.isSuccess ? (
          <span role="status" className="text-sm text-fg-secondary">
            {t("settings.saved")}
          </span>
        ) : null}
      </div>
      <AppDialog
        open={confirming}
        onClose={() => {
          setConfirming(false);
          save.reset();
        }}
        title={t("settings.general.widenTitle")}
        description={
          visibility === "private"
            ? undefined
            : t(`settings.general.widen.${visibility}`, { entity })
        }
      >
        <div className="flex flex-col gap-4">
          {save.isError ? <Notice tone="danger">{projectRefusal(save.error)}</Notice> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              {t("settings.cancel")}
            </Button>
            <Button type="button" disabled={save.isPending} onClick={() => save.mutate(body())}>
              {t("settings.general.widenConfirm")}
            </Button>
          </DialogFooter>
        </div>
      </AppDialog>
    </form>
  );
}
